import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getPostBySlug, getPosts } from '../lib/store';
import { formatDate } from '../lib/utils';
import TableOfContents from '../components/TableOfContents';
import BookPromo from '../components/BookPromo';
import SafeIcon from '../common/SafeIcon';
import { FiClock, FiUser, FiFolder, FiArrowLeft } from 'react-icons/fi';

const PostDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [similarPosts, setSimilarPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        const foundPost = await getPostBySlug(slug);
        if (!foundPost) {
          navigate('/');
          return;
        }
        setPost(foundPost);

        const all = await getPosts(true);
        const similar = all
          .filter(p => p.category === foundPost.category && p.id !== foundPost.id)
          .slice(0, 2);
        setSimilarPosts(similar);
      } catch (error) {
        console.error('Error fetching post:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPost();
    window.scrollTo(0, 0);
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-finance-green"></div>
      </div>
    );
  }

  if (!post) return null;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": window.location.href
    },
    "headline": post.title,
    "description": post.description,
    "datePublished": post.date,
    "dateModified": post.date,
    "author": {
      "@type": "Person",
      "name": post.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Dividenden Investor",
      "logo": {
        "@type": "ImageObject",
        "url": "https://blog.dividenden-investor.ch/logo.png"
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>{post.title} | Dividenden Investor DACH</title>
        <meta name="description" content={post.description} />
        <meta name="keywords" content={post.focus_keyword} />
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      <div className="mb-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-finance-green transition">
          <SafeIcon icon={FiArrowLeft} /> Zurück zur Übersicht
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <article className="lg:col-span-8">
          <header className="mb-10">
            <div className="flex flex-wrap items-center gap-4 text-sm font-medium text-slate-500 mb-4">
              <span className="flex items-center gap-1 text-finance-green bg-emerald-50 px-3 py-1 rounded-full">
                <SafeIcon icon={FiFolder} /> {post.category}
              </span>
              <span className="flex items-center gap-1">
                <SafeIcon icon={FiClock} /> {post.read_time || '5'} Min. Lesezeit
              </span>
              <span><time dateTime={post.date}>Veröffentlicht am {formatDate(post.date)}</time></span>
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-finance-blue leading-tight mb-6">
              {post.title}
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed font-medium">
              {post.description}
            </p>
          </header>

          <div className="prose prose-lg prose-slate max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {post.content}
            </ReactMarkdown>
          </div>

          <footer className="mt-16 pt-8 border-t border-slate-200">
            <div className="flex items-center gap-4 bg-slate-50 p-6 rounded-xl">
              <div className="w-16 h-16 bg-finance-blue text-white rounded-full flex items-center justify-center text-2xl flex-shrink-0">
                <SafeIcon icon={FiUser} />
              </div>
              <div>
                <h4 className="font-bold text-finance-blue text-lg mb-1">Über den Autor: {post.author}</h4>
                <p className="text-slate-600 text-sm">
                  Finanzenthusiast und passionierter Verfechter der Dividendenstrategie. Teilt sein Wissen, um anderen den Weg zur finanziellen Unabhängigkeit im DACH-Raum zu erleichtern.
                </p>
              </div>
            </div>
          </footer>
        </article>

        <aside className="lg:col-span-4 space-y-8">
          <BookPromo variant="sidebar" />
          
          <TableOfContents content={post.content} />

          {similarPosts.length > 0 && (
            <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
              <h3 className="text-lg font-bold text-finance-blue mb-4">Ähnliche Beiträge</h3>
              <ul className="space-y-4">
                {similarPosts.map(sp => (
                  <li key={sp.id}>
                    <Link to={`/post/${sp.slug}`} className="group block">
                      <h4 className="font-semibold text-slate-800 group-hover:text-finance-green transition-colors line-clamp-2 mb-1">
                        {sp.title}
                      </h4>
                      <p className="text-xs text-slate-500">{formatDate(sp.date)}</p>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </>
  );
};

export default PostDetail;